import { Sorting } from '@devexpress/dx-react-grid'
import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import {
  Consolidation,
  ConsolidationOutboundCreateInput,
  ConsolidationType,
} from '@returnmates/client-core/src/graphql/generated/api'
import {
  getContainersCountSelector,
  getContainersSelector,
} from '@returnmates/client-core/src/selectors/admin'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import { FORM_ERROR } from 'final-form'
import moment from 'moment-timezone'
import { memo, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import ConsolidationsTable from '../../../components/ConsolidationsTable'
import { ConsolidationFilter } from '../../../components/ConsolidationsTable/types'
import { FLYOUT_SIDEBAR_TYPES, FlyoutSidebarTypes } from '../../../constants/flyoutSidebar'
import { Context } from '../../Main'
import { DEFAULT_SORT, SORT_DIRECTION_MAP, SORT_FIELDS_MAP } from '../constants'
import { getFormattedData } from '../utils'
import AddConsolidationModal from './AddConsolidationModal'

function ContainersSection() {
  const dispatch = useDispatch()
  const containers = useSelector(getContainersSelector)
  const containersCount = useSelector(getContainersCountSelector)
  const { currentDateRange, currentHubId } = useContext(Context)
  const [isLoading, setIsLoading] = useState(false)
  const [pageSize, setPageSize] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [sorting, setSorting] = useState<Sorting>(DEFAULT_SORT)
  const [filter, setFilter] = useState<ConsolidationFilter>({
    status: null,
    partnerCode: null,
    carrierId: null,
    consolidationId: null,
    locationId: null,
    addressId: null,
  })
  const [isAddConsolidationModalOpen, setIsAddConsolidationModalOpen] = useState(false)

  const openContainerDataRef = useRef<Consolidation | null>(null)

  const updateOpenContainerDataRef = useCallback(
    (val: Consolidation) => (openContainerDataRef.current = val),
    [],
  )

  const onSortingChange = useCallback(newSorting => {
    setSorting(newSorting || DEFAULT_SORT)
  }, [])

  const handleOpenAddContainerModal = useCallback(() => {
    setIsAddConsolidationModalOpen(true)
  }, [])

  const handleCloseAddContainerModal = useCallback(() => {
    setIsAddConsolidationModalOpen(false)
  }, [])

  const onFilterUpdate = useCallback(
    newFilter => {
      setCurrentPage(0)
      setFilter(newFilter)
    },
    [setCurrentPage],
  )

  const createContainer = useCallback(
    async ({
      hubIdOrigin,
      hubIdDestination,
      expectedDeliveryDate,
    }: ConsolidationOutboundCreateInput) => {
      setIsLoading(true)
      try {
        await createAsyncAction(
          dispatch,
          actions.createOutboundConsolidation.request({
            input: {
              hubIdOrigin,
              hubIdDestination,
              expectedDeliveryDate: moment(expectedDeliveryDate).format('YYYY-MM-DD'),
            },
          }),
        )
        setIsAddConsolidationModalOpen(false)
      } catch (err) {
        const { message } = err as Error

        return {
          [FORM_ERROR]: errorMapper(message || (err as string)),
        }
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch],
  )

  const requestContainers = useCallback(async () => {
    setIsLoading(true)
    try {
      const searchFilter = Object.entries(filter).find(
        item =>
          (item[0] === 'destination' ||
            item[0] === 'packageId' ||
            item[0] === 'trackingId' ||
            item[0] === 'consolidationId') &&
          item[1],
      )
      const searchFilterWhere = searchFilter ? { [searchFilter[0]]: searchFilter[1] } : null

      await createAsyncAction(
        dispatch,
        actions.getContainers.request({
          where: searchFilterWhere || {
            fromDate: moment(currentDateRange.startDate).format('YYYY-MM-DD'),
            toDate: moment(currentDateRange.endDate).format('YYYY-MM-DD'),
            hubId: currentHubId,
            type: ConsolidationType.OUTBOUND,
            ...filter,
          },
          pagination: {
            size: pageSize,
            page: currentPage,
          },
          order: {
            direction: SORT_DIRECTION_MAP[sorting.direction],
            field: SORT_FIELDS_MAP[sorting.columnName as keyof typeof SORT_FIELDS_MAP],
          },
        }),
      )

      if (openContainerDataRef.current?.id) {
        await createAsyncAction(
          dispatch,
          actions.getConsolidationPackages.request({
            where: {
              consolidationId: openContainerDataRef.current?.id,
            },
            pagination: {
              size: 100,
            },
          }),
        )
      }
    } catch (err) {
      const { message } = err as Error

      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.ERROR,
          message: errorMapper(message || (err as string)),
        }),
      )
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentDateRange.endDate,
    currentDateRange.startDate,
    currentHubId,
    currentPage,
    dispatch,
    filter,
    pageSize,
    sorting.columnName,
    sorting.direction,
  ])

  useEffect(() => {
    if (location.search) {
      const searchParams = new URLSearchParams(location.search)
      const locationId = searchParams.get('locationId')

      if (locationId) {
        onFilterUpdate({
          //@ts-ignore
          locationId,
          fromDate: undefined,
          toDate: undefined,
          hubId: undefined,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, onFilterUpdate])

  useEffect(() => {
    dispatch(actions.getCarriers.request())
  }, [dispatch])

  useEffect(() => {
    if (pageSize) {
      requestContainers()
    }
  }, [pageSize, requestContainers])

  return (
    <div>
      <ConsolidationsTable
        data={getFormattedData(containers || [])}
        totalCount={containersCount || 0}
        onFilterUpdate={onFilterUpdate}
        onSortingChange={onSortingChange}
        onPageSizeChange={setPageSize}
        onCurrentPageChange={setCurrentPage}
        currentPageNumber={currentPage}
        isLoading={isLoading}
        updateOpenConsolidationDataRef={updateOpenContainerDataRef}
        handleOpenAddConsolidationModal={handleOpenAddContainerModal}
        tableType={FLYOUT_SIDEBAR_TYPES.EDIT_CONTAINER as FlyoutSidebarTypes.EDIT_CONTAINER}
      />

      <AddConsolidationModal
        isOpen={isAddConsolidationModalOpen}
        onClose={handleCloseAddContainerModal}
        onCreate={createContainer}
        isLoading={isLoading}
      />
    </div>
  )
}

export default memo(ContainersSection)
